import { useEffect, useRef, useState } from 'react';
import { getHubs, getStickies } from '@/services/mockData';
import { TAB_CANVAS_HUBS } from '../shell/constants';
import { useCanvasTransform } from '../../hooks/useCanvasTransform';
import { HubNode } from './HubNode';
import { Sticky } from './Sticky';
import { ZoomCtrl } from './ZoomCtrl';
import { CanvasHint } from './CanvasHint';

// Design-canonical positions and idx overrides, keyed by slug.
// Each tab defines its own grid layout; slugs are unique so there is no collision.
const HUB_OVERRIDES = {
  // Proposition Builder — 3×2 grid
  niche:         { pos_x: 80,  pos_y: 100, idx: '01' },
  brand:         { pos_x: 430, pos_y: 100, idx: '02' },
  content:       { pos_x: 780, pos_y: 100, idx: '03' },
  buyer:         { pos_x: 80,  pos_y: 330, idx: '04' },
  roadmap:       { pos_x: 430, pos_y: 330, idx: '05' },
  added:         { pos_x: 780, pos_y: 330, idx: '06' },
  // Pipeline Builder — 2×2 grid
  opportunities: { pos_x: 80,  pos_y: 100, idx: '01' },
  articles:      { pos_x: 430, pos_y: 100, idx: '02' },
  contacts:      { pos_x: 80,  pos_y: 330, idx: '03' },
  nurture:       { pos_x: 430, pos_y: 330, idx: '04' },
  // Delivery Dashboard — single row
  portfolio:     { pos_x: 80,  pos_y: 100, idx: '01' },
  value:         { pos_x: 430, pos_y: 100, idx: '02' },
};

export function Canvas({ tab, activeHub, onOpenHub }) {
  const wrapRef = useRef(null);
  const { transform, fit, zoomBy, bindWheel, dragHandlers } = useCanvasTransform();

  const [hubs, setHubs] = useState([]);
  const [stickies, setStickies] = useState([]);
  const [error, setError] = useState(null);

  // Reset the canvas view origin whenever the active tab changes.
  useEffect(() => { fit(); }, [tab, fit]);

  useEffect(() => {
    let cancelled = false;
    const allowed = new Set(TAB_CANVAS_HUBS[tab] ?? []);
    Promise.all([getHubs(), getStickies()])
      .then(([h, s]) => {
        if (cancelled) return;
        const visible = h
          .filter((hub) => allowed.has(hub.slug))
          .map((hub) => ({ ...hub, ...(HUB_OVERRIDES[hub.slug] || {}) }));
        setHubs(visible);
        setStickies(tab === 'proposition' ? s : []);
      })
      .catch((e) => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, [tab]);

  useEffect(() => {
    return bindWheel(wrapRef.current);
  }, [bindWheel]);

  if (error) {
    return (
      <div className="absolute inset-0 grid place-items-center font-mono text-[10px] text-danger uppercase">
        CANVAS LOAD FAILED
      </div>
    );
  }

  return (
    <div
      ref={wrapRef}
      className="canvas-wrap bg-canvas-grid"
      {...dragHandlers}
    >
      <div
        className="canvas-layer"
        style={{ transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.k})` }}
      >
        {stickies.map((s) => (
          <Sticky key={s.id} note={s} />
        ))}

        {hubs.map((h) => (
          <HubNode
            key={h.id}
            hub={h}
            active={activeHub === h.slug}
            pulse={false}
            onOpen={onOpenHub}
          />
        ))}
      </div>

      <CanvasHint />
      <ZoomCtrl
        k={transform.k}
        onZoomIn={() => zoomBy(0.1)}
        onZoomOut={() => zoomBy(-0.1)}
        onFit={fit}
      />
    </div>
  );
}

export default Canvas;
